import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Shield, Lock, TrendingUp, ArrowRight, CheckCircle, FileCheck, Zap, HeartHandshake } from "lucide-react";

const VKB_LOGO_URL = "https://customer-assets.emergentagent.com/job_ingenieur-agency/artifacts/7oos7e3e_VKBayern_logo.svg.png";
const MANSFELD_IMAGE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/8u6zz0by_490df1d7-396a-450c-8096-d69de69384f5.JPG";
// Looking up image - VKB sign
const MANSFELD_LOOKING = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/jk90s8h1_ChatGPT%20Image%2028.%20Apr.%202026%2C%2013_17_08.png";

export default function LeistungenPage() {
  const mainServices = [
    {
      id: "berufshaftpflicht",
      icon: Shield,
      title: "Berufshaftpflicht",
      subtitle: "Kernstück Ihrer Existenzsicherung",
      description: "Die Berufshaftpflichtversicherung schützt Sie vor Risiken wie Planungs-, Beratungs-, Bauüberwachungs- oder Koordinationsfehler, denen Sie als Architekt oder Ingenieur täglich ausgesetzt sind.",
      features: [
        "Absicherung von Planungs- und Beratungsfehlern",
        "Schutz bei Bauüberwachungs- und Koordinationsfehlern",
        "Abwehr unberechtigter Ansprüche",
        "Deckung für alle Leistungsphasen nach HOAI",
        "Nachhaftung für abgeschlossene Projekte"
      ],
      highlight: "Ein einziger Planungsfehler kann existenzbedrohend sein."
    },
    {
      id: "cyber",
      icon: Lock,
      title: "Cyberschutz",
      subtitle: "Schutz in der digitalen Welt",
      description: "Ob Daten der Kunden, vertrauliche betriebliche Informationen oder Steuerung der internen Prozesse – die Cyber-Angriffe werden professioneller und auch internationaler.",
      features: [
        "Absicherung bei Datenpannen",
        "Schutz bei Betriebsunterbrechung",
        "Kostenübernahme für Datenwiederherstellung",
        "Präventionsberatung und Notfallhilfe",
        "Haftpflichtschutz bei Datenschutzverletzungen"
      ],
      highlight: "Digitale Angriffe nehmen rasant zu."
    },
    {
      id: "altersvorsorge",
      icon: TrendingUp,
      title: "Betriebliche Altersvorsorge",
      subtitle: "Für Ihre Zukunft",
      description: "Die betriebliche Altersvorsorge ist ein unverzichtbarer Baustein moderner Personalpolitik. Sie eröffnet eine attraktive Möglichkeit, Ihren Mitarbeitern eine wertvolle Zusatzleistung zu bieten.",
      features: [
        "Attraktive Zusatzleistung für Mitarbeiter",
        "Steuerliche Vorteile nutzen",
        "Mitarbeiterbindung stärken",
        "Eigene Altersvorsorge aufbauen",
        "Flexible Gestaltungsmöglichkeiten"
      ],
      highlight: "Fachkräfte gewinnen und binden."
    }
  ];

  const faqs = [
    {
      question: "Brauche ich als Ingenieur zwingend eine Berufshaftpflicht?",
      answer: "Ja, als Ingenieur sind Sie für Ihre planerischen Leistungen haftbar. Eine Berufshaftpflichtversicherung schützt Sie vor den finanziellen Folgen von Planungsfehlern und ist bei vielen Auftraggebern vertraglich vorgeschrieben. Sie ist das Fundament Ihrer beruflichen Existenzsicherung."
    },
    {
      question: "Was ist mit Bauüberwachung – bin ich da auch versichert?",
      answer: "Ja, eine gute Berufshaftpflichtversicherung für Architekten und Ingenieure deckt alle Leistungsphasen nach HOAI ab, einschließlich der Bauüberwachung. Wichtig ist, dass Ihre Deckungssumme und der Versicherungsumfang zu Ihren tatsächlichen Tätigkeiten passen."
    },
    {
      question: "Was passiert im Schadenfall?",
      answer: "Im Schadenfall sind wir Ihr erster Ansprechpartner. Wir melden den Schaden für Sie beim Versicherer, koordinieren die Kommunikation und begleiten Sie durch den gesamten Prozess. Die Versicherung prüft den Anspruch und wehrt unberechtigte Forderungen ab oder reguliert berechtigte Ansprüche."
    },
    {
      question: "Ihre Vorteile bei einer Zusammenarbeit",
      answer: "Als gebundene Agentur der Versicherungskammer haben Sie bei uns einen festen Ansprechpartner, der Ihre Situation kennt und langfristig betreut. Keine wechselnden Ansprechpartner – sondern persönliche Betreuung mit klarer Zuständigkeit. Sie sind immer Kunde der Gesellschaft – ohne Zusatzkosten."
    },
    {
      question: "Was kostet eine Berufshaftpflicht für Architekten?",
      answer: "Die Kosten hängen von verschiedenen Faktoren ab: Ihrer Tätigkeit, dem Jahresumsatz, der gewünschten Deckungssumme, der gewünschten Selbstbeteiligung und Ihrer Schadenhistorie. In einem persönlichen Gespräch ermitteln wir den für Sie optimalen Schutz zu einem fairen Preis."
    },
    {
      question: "Kann ich meinen bestehenden Vertrag wechseln?",
      answer: "Ja, ein Wechsel ist in der Regel zum Ende der Vertragslaufzeit möglich. Wir prüfen Ihren bestehenden Vertrag, zeigen eventuelle Deckungslücken auf und begleiten Sie beim Wechsel – ohne Unterbrechung Ihres Versicherungsschutzes."
    }
  ];

  return (
    <div data-testid="leistungen-page" className="pt-8">
      {/* Hero Section - Clean Design without overlap */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#003B70] to-[#002855] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sky-300 font-semibold text-sm uppercase tracking-wider">Unsere Leistungen</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Versicherungslösungen für Architekten & Ingenieure
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-8">
              Klar, spezialisiert, betreut. Wir kennen die Risiken Ihrer Branche und bieten Ihnen 
              passgenaue Absicherung mit Weitsicht und Erfahrung.
            </p>
            <div className="grid grid-cols-2 gap-y-3 gap-x-8 mb-10">
              <div className="flex items-center gap-2 text-white/90">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span>Spezialisiert auf Planungsrisiken</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span>Direkte Betreuung</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span>Bundesweit tätig</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                <span>Expertise seit 25+ Jahren</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services - Uniform Cards */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Kernleistungen</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Drei Säulen Ihrer Absicherung
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Diese drei Bausteine bilden das Fundament für die Sicherheit Ihres Planungsbüros.
            </p>
          </div>

          <div className="space-y-8">
            {mainServices.map((service, index) => (
              <div 
                key={service.id}
                id={service.id}
                data-testid={`service-detail-${service.id}`}
                className="bg-white rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Content Side */}
                  <div className="p-10 md:p-12">
                    <div className="flex items-center gap-6 mb-6">
                      <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#003B70] to-[#002855] shadow-lg">
                        <service.icon className="h-8 w-8 text-white" />
                      </div>
                      <span className="text-sm font-medium text-[#003B70] uppercase tracking-wider">
                        {service.subtitle}
                      </span>
                    </div>
                    
                    <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
                      {service.title}
                    </h3>
                    
                    <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="p-4 rounded-xl bg-[#003B70]/5 border border-[#003B70]/10 mb-8">
                      <p className="text-sm font-medium text-[#003B70]">
                        Wichtig zu wissen:
                      </p>
                      <p className="text-slate-700">
                        {service.highlight}
                      </p>
                    </div>
                    
                    <Link to="/kontakt">
                      <Button className="bg-[#003B70] text-white hover:bg-[#002855] px-8 py-5 font-semibold shadow-lg">
                        Jetzt Beratung anfragen
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Features Side */}
                  <div className="p-10 md:p-12 bg-slate-50">
                    <h4 className="text-lg font-semibold text-slate-900 mb-6">
                      Ihre Vorteile im Überblick:
                    </h4>
                    <ul className="space-y-4">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#003B70]/10">
                            <CheckCircle className="h-5 w-5 text-[#003B70]" />
                          </div>
                          <span className="text-lg text-slate-700">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Berater CTA */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-[#003B70]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky-400/20 to-blue-600/20 rounded-2xl blur-3xl" />
                <div className="relative w-56 h-56 lg:w-72 lg:h-72 rounded-2xl overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img 
                    src={MANSFELD_LOOKING}
                    alt="Jens-Oliver Mansfeld"
                    className="w-full h-full object-cover object-[70%_center]"
                  />
                </div>
              </div>
            </div>
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Persönliche Beratung<br />
                <span className="text-sky-300">von Experte zu Experte</span>
              </h2>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Als Spezialist für Architekten und Ingenieure kenne ich die Risiken Ihrer Branche. 
                Lassen Sie uns gemeinsam die optimale Absicherung für Ihr Büro finden.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/kontakt">
                  <Button className="bg-white text-[#003B70] hover:bg-slate-100 px-8 py-5 font-semibold shadow-lg">
                    Kontakt aufnehmen
                  </Button>
                </Link>
                <a href="tel:+493033937950">
                  <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8 py-5">
                    +49 30 33937950
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-section" className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Häufig gestellte Fragen
            </h2>
            <p className="text-lg text-slate-600">
              Antworten auf die wichtigsten Fragen rund um Versicherungen für Architekten und Ingenieure.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  data-testid={`faq-item-${index}`}
                  className="border-b border-slate-200 last:border-0"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-slate-900 hover:text-[#003B70] py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Noch Fragen?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Vereinbaren Sie einen unverbindlichen Beratungstermin und lassen Sie sich 
            individuell zu Ihren Versicherungsfragen beraten.
          </p>
          <Link to="/kontakt">
            <Button 
              data-testid="leistungen-cta"
              className="bg-[#003B70] hover:bg-[#002855] text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Jetzt Beratung anfragen
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
